import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit'
import { axiosInterceptor, apiCall } from 'services'
import type { IChatHeader } from 'features/Chat/Chat.types'

/**
 * DM conversation headers, the single source of truth for the inbox.
 *
 * ChatProvider used to fetch these separately through
 * `GET /api/chats/headers/user/:id` while this slice fetched the same rows from
 * `GET /api/chats/headers`, so the app held two answers to one question. The
 * header badge needs one. ChatProvider now reads from here, and the per-user
 * route (which took the id from the URL and so served anyone's inbox) is gone.
 */

// The API is same-origin and these paths already carry '/api', so the base
// stays empty to avoid doubling the prefix. Matches features/Chat/services.
const api = axiosInterceptor('')

interface ChatSliceState {
	entities: Record<string, IChatHeader>
	ids: string[]
	isLoading: boolean
	isInitialized: boolean
	error: string | null
	sentEntities: Record<string, any>
	sentIds: string[]
	receivedEntities: Record<string, any>
	receivedIds: string[]
}

const initialState: ChatSliceState = {
	entities: {},
	ids: [],
	isLoading: false,
	isInitialized: false,
	error: null,
	sentEntities: {},
	sentIds: [],
	receivedEntities: {},
	receivedIds: [],
}

export const getChatHeadersByUID = createAsyncThunk(
	'chats/getChatHeadersByUID',
	async (_: void, { rejectWithValue }) => {
		try {
			return await apiCall(api.get('/api/chats/headers'))
		} catch (error) {
			return rejectWithValue('Failed to load conversations')
		}
	},
)

export const getChatsSentByUserId = createAsyncThunk(
	'chats/getSentChats',
	async (_: void, { rejectWithValue }) => {
		try {
			return await apiCall(api.get('/api/chats/sentbyuser'))
		} catch (error) {
			return rejectWithValue('No Chats Found')
		}
	},
)

export const getChatsReceivedByUserId = createAsyncThunk(
	'chats/getReceivedChats',
	async (_: void, { rejectWithValue }) => {
		try {
			return await apiCall(api.get('/api/chats/receivedbyuser'))
		} catch (error) {
			return rejectWithValue('No Chats Found')
		}
	},
)

const chatsSlice = createSlice({
	name: 'chats',
	initialState,
	reducers: {
		/**
		 * Inserts or refreshes one header, moving it to the top of the inbox.
		 * An incoming message used to trigger a full inbox refetch; this makes
		 * it a local update, so a busy conversation is not one round trip per
		 * message.
		 */
		chatHeaderUpserted(state, action: { payload: IChatHeader }) {
			const header = action.payload
			if (!header?._id) return
			state.entities[header._id] = { ...state.entities[header._id], ...header }
			state.ids = [header._id, ...state.ids.filter(id => id !== header._id)]
		},
		/** Clears the unread dot locally so the badge drops without a round trip. */
		chatHeaderMarkedRead(state, action: { payload: { eventId: string; userId: string } }) {
			const { eventId, userId } = action.payload
			const header = state.entities[eventId]
			if (!header || !userId) return
			if (!header.readByIds?.includes(userId)) {
				header.readByIds = [...(header.readByIds || []), userId]
			}
		},
		/** Dispatch on logout so the next user does not inherit an inbox. */
		chatHeadersCleared() {
			return initialState
		},
	},
	extraReducers: builder => {
		builder
			.addCase(getChatHeadersByUID.pending, state => {
				state.isLoading = true
			})
			.addCase(getChatHeadersByUID.fulfilled, (state, action) => {
				const chats: IChatHeader[] = action.payload || []
				state.entities = {}
				state.ids = chats.map(chat => {
					state.entities[chat._id] = chat
					return chat._id
				})
				state.isLoading = false
				state.isInitialized = true
			})
			.addCase(getChatHeadersByUID.rejected, (state, action) => {
				state.isLoading = false
				state.isInitialized = true
				state.error = action.error.message ?? null
			})

			.addCase(getChatsSentByUserId.pending, state => {
				state.isLoading = true
			})
			.addCase(getChatsSentByUserId.fulfilled, (state, action) => {
				const chats = action.payload || []
				state.sentIds = chats.map(chat => {
					state.sentEntities[chat._id] = chat
					return chat._id
				})
				state.isLoading = false
				state.isInitialized = true
			})
			.addCase(getChatsSentByUserId.rejected, (state, action) => {
				state.isLoading = false
				state.isInitialized = true
				state.error = action.error.message ?? null
			})

			.addCase(getChatsReceivedByUserId.pending, state => {
				state.isLoading = true
			})
			.addCase(getChatsReceivedByUserId.fulfilled, (state, action) => {
				const chats = action.payload || []
				state.receivedIds = chats.map(chat => {
					state.receivedEntities[chat._id] = chat
					return chat._id
				})
				state.isLoading = false
				state.isInitialized = true
			})
			.addCase(getChatsReceivedByUserId.rejected, (state, action) => {
				state.isLoading = false
				state.isInitialized = true
				state.error = action.error.message ?? null
			})
	},
})

export const { chatHeaderUpserted, chatHeaderMarkedRead, chatHeadersCleared } = chatsSlice.actions

// The slice is mounted under `chatHeaders`, not `chats`. See slices/index.js.
const selectSlice = (state: any): ChatSliceState => state.chatHeaders
const selectUserId = (state: any): string | undefined => state.userLogin?.userInfo?._id

export const selectChatHeaders = createSelector([selectSlice], slice =>
	slice.ids.map(id => slice.entities[id]).filter(Boolean),
)

export const selectChatHeadersInitialized = createSelector(
	[selectSlice],
	slice => slice.isInitialized,
)

/**
 * Conversations with at least one message the current user has not seen. This
 * is a count of threads, not of messages, which is what the header badge shows
 * and what `readByIds` can actually answer.
 */
export const selectUnreadChatCount = createSelector(
	[selectChatHeaders, selectUserId],
	(headers, userId) =>
		userId ? headers.filter(h => !(h.readByIds || []).includes(userId)).length : 0,
)

export default chatsSlice
